import React, { useMemo, useState, useEffect } from 'react';
import NumericKeypad from '../common/NumericKeypad';

const resolveQuickPickPrice = (product) => {
  if (!product) return 0;
  const price = Number(product.price);
  if (Number.isFinite(price)) return price;
  const base = Number(product.basePrice);
  return Number.isFinite(base) ? base : 0;
};

const QuickPickModal = ({
  category,
  initialSubcategory = '',
  products = [],
  isLoading = false,
  error = '',
  onClose,
  onAdd,
}) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantity, setQuantity] = useState('1');
  const [validationError, setValidationError] = useState('');
  const [replaceOnNext, setReplaceOnNext] = useState(false);
  const [selectedSubcategory, setSelectedSubcategory] = useState('');

  useEffect(() => {
    setSelectedProduct(null);
    setQuantity('1');
    setValidationError('');
    setReplaceOnNext(false);
    setSelectedSubcategory(initialSubcategory || '');
  }, [category, initialSubcategory]);

  const sortedProducts = useMemo(() => {
    return [...products].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [products]);

  const subcategoryOptions = useMemo(() => {
    if (!category || !Array.isArray(category.subcategories)) return [];
    const seen = new Set();
    return category.subcategories
      .map((entry) => (typeof entry === 'string' ? entry.trim() : ''))
      .filter(Boolean)
      .filter((entry) => {
        const key = entry.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => a.localeCompare(b));
  }, [category]);

  const filteredProducts = useMemo(() => {
    if (!selectedSubcategory) return sortedProducts;
    const target = selectedSubcategory.trim().toLowerCase();
    return sortedProducts.filter((product) => {
      const value = typeof product.subcategory === 'string' ? product.subcategory.trim().toLowerCase() : '';
      return value === target;
    });
  }, [sortedProducts, selectedSubcategory]);

  useEffect(() => {
    if (!selectedSubcategory) return;
    const target = selectedSubcategory.trim().toLowerCase();
    const exists = subcategoryOptions.some(
      (entry) => entry.trim().toLowerCase() === target
    );
    if (!exists) {
      setSelectedSubcategory('');
    }
  }, [subcategoryOptions, selectedSubcategory]);

  useEffect(() => {
    if (!selectedProduct) return;
    const exists = filteredProducts.some((product) => product._id === selectedProduct._id);
    if (!exists) {
      setSelectedProduct(null);
      setQuantity('1');
      setValidationError('');
      setReplaceOnNext(false);
    }
  }, [filteredProducts, selectedProduct]);

  if (!category) return null;

  const handleConfirm = () => {
    const parsed = Number(quantity);
    if (!selectedProduct) {
      setValidationError('Select a product first.');
      return;
    }
    if (Number.isNaN(parsed) || parsed <= 0) {
      setValidationError('Enter a quantity greater than zero.');
      return;
    }
    onAdd(selectedProduct, parsed);
    setSelectedProduct(null);
    setQuantity('1');
    setValidationError('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-6xl flex flex-col max-h-[calc(100vh-2rem)] relative overflow-hidden">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 min-h-11 min-w-11 px-3 py-2 text-sm font-semibold rounded-full border border-slate-300 text-slate-700 hover:border-primary hover:text-primary bg-white"
        >
          Close
        </button>
        <div className="flex shrink-0 max-h-[35vh] overflow-y-auto items-start justify-between gap-4 px-4 sm:px-6 py-5 border-b border-slate-200">
          <div className="flex flex-col gap-3">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">Quick pick</h2>
              <p className="text-sm text-slate-500 mt-1">{category.name}</p>
            </div>
            {subcategoryOptions.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedSubcategory('')}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    selectedSubcategory
                      ? 'border-slate-200 text-slate-600 hover:border-primary/40'
                      : 'border-primary bg-primary/10 text-primary'
                  }`}
                >
                  All
                </button>
                {subcategoryOptions.map((subcategory) => {
                  const isActive = selectedSubcategory === subcategory;
                  return (
                    <button
                      key={subcategory}
                      type="button"
                      onClick={() => setSelectedSubcategory(subcategory)}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                        isActive
                          ? 'border-primary bg-primary text-white'
                          : 'border-slate-200 text-slate-600 hover:border-primary/40'
                      }`}
                    >
                      {subcategory}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-5">
          {error && <p className="text-sm text-rose-600 mb-3">{error}</p>}
          {isLoading ? (
            <div className="flex h-full items-center justify-center text-slate-500">Loading products…</div>
          ) : (
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px] min-h-0 items-stretch">
              <div className="min-h-0">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 overflow-y-auto pr-1 max-h-[70vh]">
                  {filteredProducts.map((product) => {
                    const isActive = selectedProduct?._id === product._id;
                    const price = resolveQuickPickPrice(product);
                    return (
                      <button
                        key={product._id}
                        type="button"
                        onClick={() => {
                          setSelectedProduct(product);
                          setValidationError('');
                          setReplaceOnNext(true);
                        }}
                        className={`border rounded-2xl p-4 text-left transition ${
                          isActive ? 'border-primary bg-primary/5' : 'border-slate-200 hover:border-primary/40'
                        }`}
                      >
                        <p className="text-sm font-semibold text-slate-900 line-clamp-2">{product.name}</p>
                        <p className="text-xs text-slate-500 mt-2">Stock: {product.stock ?? '—'}</p>
                        <p className="text-lg font-bold text-primary mt-2">${price.toFixed(2)}</p>
                      </button>
                    );
                  })}
                  {filteredProducts.length === 0 && !isLoading && (
                    <div className="col-span-full text-center text-slate-500 py-8">
                      {selectedSubcategory
                        ? 'No products found for this subcategory.'
                        : 'No products found for this category.'}
                    </div>
                  )}
                </div>
              </div>
              <div className="border border-slate-200 rounded-2xl p-4 flex flex-col gap-4 h-full lg:min-h-[70vh] lg:sticky lg:top-6">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Quantity</p>
                  {!selectedProduct && (
                    <p className="text-sm text-slate-500 mt-2">
                      Select a product to enter quantity.
                    </p>
                  )}
                  {selectedProduct && (
                    <p className="text-xs text-slate-500 mt-1">{selectedProduct.name}</p>
                  )}
                </div>
                {selectedProduct && (
                  <>
                    <div className="text-center py-2">
                      <p
                        className={`text-4xl font-bold text-slate-900 inline-block px-4 py-1 rounded-xl ${
                          replaceOnNext ? 'ring-2 ring-primary/50 bg-primary/10' : ''
                        }`}
                      >
                        {quantity || '0'}
                      </p>
                      {validationError && <p className="text-sm text-rose-600 mt-1">{validationError}</p>}
                    </div>
                    <NumericKeypad
                      value={quantity}
                      onChange={(next) => {
                        setQuantity(next);
                        if (validationError) setValidationError('');
                      }}
                      allowDecimal={false}
                      replaceOnNext={replaceOnNext}
                      onReplaceHandled={() => setReplaceOnNext(false)}
                    />
                    <button
                      type="button"
                      onClick={handleConfirm}
                      className="w-full rounded-xl bg-primary text-white py-3 font-semibold hover:bg-primary/90"
                    >
                      Add to cart
                    </button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickPickModal;
